import Result from '../../common/result';
import validationService from '../../services/validation';
import {
  Step,
  GetStepRequest,
  ProcessStepRequest,
  StepResult,
  ValidationStepStatus,
  ValidationStep,
  SelfieStepParameters,
  AppIdStepParameters,
  TermsStepValue,
} from '../../services/validation/validationService';
import { Authentication } from '../../services/faceAuthentication';
import WindowProvider from '../../common/window';
import { CustomError } from '../../common/errors';
import StepError from './stepError';

/** Callback function that receives a step and returns a result with an extracted value */
type GetStepSuccessValue<TSuccess> = (step: Step) => Result<StepError, TSuccess>;
/** Callback function that receives a step and returns a result with no custom value */
const noValueSuccess: GetStepSuccessValue<boolean> = () => Result.noValueSuccess();

/**
 * Process a step result
 * 1. Check if the result is failed, return error
 * 2. Check if the step is invalid, return error with error codes
 * 3. Return success by using the given callback function
 */
function processStepResult<TSuccess>(
  stepName: ValidationStep,
  stepResult: StepResult,
  getSuccessValue: GetStepSuccessValue<TSuccess>,
): Result<StepError, TSuccess> {
  if (stepResult.isFailed()) {
    return Result.failed(StepError.fromError(stepName, stepResult.failure()));
  }

  const step = stepResult.success();
  if (step.status === ValidationStepStatus.invalid) {
    return Result.failed(StepError.fromFailedStep(step));
  }

  return getSuccessValue(step);
}

/** Call get step and process the result using the processStepResult function  */
async function getStep<TSuccess>(stepRequest: GetStepRequest, getSuccessValue: GetStepSuccessValue<TSuccess>) {
  const result = await validationService.getStep(stepRequest);
  return processStepResult(stepRequest.step, result, getSuccessValue);
}

/** Call update step and process the result with the processStepResult function */
async function updateStepWithResult<TSuccess = boolean>(
  stepRequest: ProcessStepRequest,
  getSuccessValue: GetStepSuccessValue<TSuccess>,
): Promise<Result<StepError, TSuccess>> {
  const result = await validationService.processStep(stepRequest);
  return processStepResult(stepRequest.step, result, getSuccessValue);
}

/** Update step and process the result by using the no value success helper function */
const updateStep = (stepRequest: ProcessStepRequest) => updateStepWithResult<boolean>(stepRequest, noValueSuccess);

/**
 * Upload the accepted terms
 *
 * @returns Result.success on success, or Result.failure
 */
function uploadAcceptedTermsStep(validationId: string, terms: TermsStepValue): Promise<Result<StepError>> {
  return updateStep({
    validationId,
    step: ValidationStep.terms,
    value: terms,
  });
}

/**
 * Upload the identity token
 *
 * @returns Result.success on success, or Result.failure
 */
function uploadIdentityTokenStep(
  validationId: string,
  authenticationResult: Authentication,
): Promise<Result<StepError>> {
  return updateStep({
    validationId,
    step: ValidationStep.identityToken,
    value: {
      identityAuthToken: authenticationResult.identityAuthToken,
    },
  });
}

/**
 * Upload the face auth token
 *
 * @returns Result.success on success, or Result.failure
 */
function uploadFaceAuthTokenStep(
  validationId: string,
  authenticationResult: Authentication,
): Promise<Result<StepError>> {
  return updateStep({
    validationId,
    step: ValidationStep.faceAuth,
    value: {
      identityAuthToken: authenticationResult.identityAuthToken,
    },
  });
}

/**
 * Load selfie image upload URL
 *
 * @returns Result.success with upload URL, or Result.failure
 */
function getSelfieUploadUrlStep(validationId: string): Promise<Result<StepError, string>> {
  return getStep(
    {
      validationId,
      step: ValidationStep.selfie,
    },
    (step: Step) => {
      const { selfieUploadUrl } = step.parameters as SelfieStepParameters;
      return Result.successful(selfieUploadUrl);
    },
  );
}

/**
 * Perform selfie image upload
 *
 * @param validationId
 * @param selfieUploadUrl - The upload url
 * @param authenticationResult - The authentication result
 * @returns Result.success on successful upload, or Result.failure
 */
async function performSelfieUploadStep(
  validationId: string,
  selfieUploadUrl: string,
  authenticationResult: Authentication,
): Promise<Result<CustomError>> {
  const uploadResult = await validationService.uploadFile({
    validationId,
    contentType: 'image/jpeg',
    uploadUrl: selfieUploadUrl,
    base64FileData: authenticationResult.base64SelfieImage as string,
  });

  if (uploadResult.isFailed()) {
    return Result.noValueFailed(uploadResult.failure());
  }

  return Result.noValueSuccess();
}

/**
 * Confirm the selfie image upload
 *
 * @returns Result.success on success, or Result.failure
 */
function confirmSelfieUploadStep(validationId: string): Promise<Result<StepError>> {
  return updateStep({
    validationId,
    step: ValidationStep.selfie,
    value: {
      upload: 'done',
    },
  });
}

/**
 * Load the document capture token
 * @returns Result.success with the token or Result.failure
 */
function getDocumentCaptureTokenStep(validationId: string): Promise<Result<StepError, string>> {
  return updateStepWithResult<string>(
    {
      validationId,
      step: ValidationStep.appId,
      value: {
        type: 'referrer',
        value: `${WindowProvider.href()}*`,
      },
    },
    (step: Step) => {
      const { token: documentCaptureToken } = step.parameters as AppIdStepParameters;
      return Result.successful(documentCaptureToken);
    },
  );
}

/**
 * Confirm the document upload is completed
 *
 * @returns Result.success on success, or Result.failure
 */
function confirmDocumentUploadStep(validationId: string): Promise<Result<StepError>> {
  return updateStep({
    validationId,
    step: ValidationStep.document,
    value: {
      upload: 'done',
    },
  });
}

/** Upload uniqueness result step */
function uploadUniquenessResultStep(
  validationId: string,
  uniquenessResult: Authentication,
): Promise<Result<StepError>> {
  return updateStep({
    validationId,
    step: ValidationStep.uniqueness,
    value: {
      identityAuthToken: uniquenessResult.identityAuthToken,
    },
  });
}

export default {
  uploadAcceptedTermsStep,
  uploadIdentityTokenStep,
  uploadFaceAuthTokenStep,
  getSelfieUploadUrlStep,
  performSelfieUploadStep,
  confirmSelfieUploadStep,
  getDocumentCaptureTokenStep,
  confirmDocumentUploadStep,
  uploadUniquenessResultStep,
};
